import { useState, useEffect, useRef } from 'react';
import $ from 'jquery';
import 'bootstrap-datepicker';
import 'bootstrap-datepicker/dist/css/bootstrap-datepicker.standalone.css';
import { useApp } from '../AppContext';
import { superAdminApi } from '../services/api';
import { SuperAdminNav } from './SuperAdminConsole';
import Chip from '../components/Chip';

const PAGE_SIZE = 10;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function pageRange(current, total) {
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end = Math.min(total, current + delta);
  const range = [];
  for (let i = start; i <= end; i++) range.push(i);
  return range;
}

const EXPIRY_COLOR = { Red: 'var(--rd)', Orange: 'var(--am)', White: 'var(--nv)' };

function fmt(n) { return Number(n || 0).toLocaleString('en-IN'); }

// "2026-06-03" (YYYY-MM-DD from date input) → "03-Jun-2026" (dd-MMM-yyyy for API)
function inputToApi(d) {
  if (!d) return '';
  const [yyyy, mm, dd] = d.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

// "2026-06-03" → "03-Jun-2026" for display
function formatDisplay(d) {
  if (!d) return '';
  const [yyyy, mm, dd] = d.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

function toIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Default range: exactly one year ago from today, through today
function defaultDateRange() {
  const now = new Date();
  const toVal = toIso(now);
  const from = new Date(now);
  from.setFullYear(from.getFullYear() - 1);
  return { fromVal: toIso(from), toVal };
}

// Text input showing dd-MMM-yyyy with a bootstrap-datepicker calendar
function DateField({ value, onChange, placeholder = 'dd-MMM-yyyy' }) {
  const ref = useRef(null);

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker({
      format: 'dd-M-yyyy',
      autoclose: true,
      todayHighlight: true,
      orientation: 'bottom auto',
    });
    $input.on('changeDate', e => onChange(toIso(e.date)));
    return () => { $input.datepicker('destroy'); $input.off('changeDate'); };
  }, []); // eslint-disable-line

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <input
        ref={ref}
        className="fsel"
        type="text"
        readOnly
        defaultValue={formatDisplay(value)}
        placeholder={placeholder}
        style={{ cursor: 'pointer', paddingRight: 28, minWidth: 130 }}
      />
      <span style={{ position: 'absolute', right: 8, cursor: 'pointer', color: 'var(--g400)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </span>
    </div>
  );
}

export default function AdminViewClients({ onNavigate }) {
  const { addToast } = useApp();
  const { fromVal: defFrom, toVal: defTo } = defaultDateRange();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState(defFrom);
  const [toDate, setToDate] = useState(defTo);
  const [appliedRange, setAppliedRange] = useState({ from: defFrom, to: defTo });
  const [exporting, setExporting] = useState(false);

  function fetchClients(from, to) {
    setLoading(true);
    superAdminApi.getClientListView(inputToApi(from), inputToApi(to))
      .then(data => setClients(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchClients(defFrom, defTo); }, []); // eslint-disable-line

  function applyDateRange() {
    setAppliedRange({ from: fromDate, to: toDate });
    setPage(1);
    fetchClients(fromDate, toDate);
  }

  function clearDateRange() {
    setFromDate('');
    setToDate('');
    setAppliedRange({ from: '', to: '' });
    setPage(1);
    fetchClients('', '');
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? clients.filter(c =>
        (c.clientname || '').toLowerCase().includes(q) ||
        (c.clientnumber || '').toLowerCase().includes(q)
      )
    : clients;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const showFrom = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const showTo = Math.min(safePage * PAGE_SIZE, filtered.length);

  function goTo(p) { setPage(Math.max(1, Math.min(p, totalPages))); }

  function openClient(c) {
    onNavigate('superadmin', { clientId: c.clientid, clientName: c.clientname });
  }

  async function handleExportExcel() {
    setExporting(true);
    try {
      const blob = await superAdminApi.exportClientListExcel(inputToApi(appliedRange.from), inputToApi(appliedRange.to));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AdminClientList.xls';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addToast('Failed to export Excel. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <SuperAdminNav onNavigate={onNavigate} onChangePassword={() => {}} currentPage="admin-view-clients" />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Clients</div>
            <div className="psub">Select a client to view their ledger, invoices and reports</div>
          </div>
          <div className="flex g8">
            <button className="btn bout" onClick={handleExportExcel} disabled={exporting}>
              {exporting ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '⬇ Export Excel'}
            </button>
          </div>
        </div>

        <div className="card mb16">
          <div className="fbar" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <div className="sw" style={{ flex: '1 1 220px', minWidth: 0 }}>
              <span className="sico">🔍</span>
              <input
                className="sinp"
                placeholder="Search"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <label className="flbl" style={{ margin: 0, whiteSpace: 'nowrap' }}>From</label>
            <DateField value={fromDate} onChange={setFromDate} placeholder="From" />
            <label className="flbl" style={{ margin: 0, whiteSpace: 'nowrap' }}>To</label>
            <DateField value={toDate} onChange={setToDate} placeholder="To" />
            <button className="btn bpri bsm" onClick={applyDateRange}>Apply</button>
            <button className="btn bghost bsm" onClick={clearDateRange}>Clear</button>
          </div>
        </div>

        <div className="card">
          <div className="twrap">
            <table>
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Client Status</th>
                  <th>Industry</th>
                  <th>OnBoarding Status</th>
                  <th>Total Added</th>
                  <th>Total Spent</th>
                  <th>Balance</th>
                  <th>Hold</th>
                  <th>Created On</th>
                  <th>Contract End Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>Loading…</td></tr>
                )}
                {!loading && pageItems.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>
                    {appliedRange.from || appliedRange.to ? 'No clients found for this date range' : 'No clients found'}
                  </td></tr>
                )}
                {!loading && pageItems.map((c, i) => (
                  <tr key={c.clientid ?? i}>
                    <td>
                      <div className="tdl" onClick={() => openClient(c)}>{c.clientname}</div>
                      <div className="tdm">{c.clientnumber}</div>
                    </td>
                    <td><Chip label={c.clientstatus || '—'} /></td>
                    <td className="tdsm">{c.industrytype || '—'}</td>
                    <td><Chip label={c.onboardingstatus || '—'} /></td>
                    <td className="tdsm" style={{ fontWeight: 700, color: 'var(--gr)' }}>₹{fmt(c.total_added)}</td>
                    <td className="tdsm" style={{ fontWeight: 700, color: 'var(--rd)' }}>₹{fmt(c.total_spent)}</td>
                    <td className="tdsm" style={{ fontWeight: 700, color: 'var(--or-dk)' }}>₹{fmt(c.availablebalance)}</td>
                    <td className="tdsm">₹{fmt(c.holdbalance)}</td>
                    <td className="tdsm">{c.createdon || '—'}</td>
                    <td className="tdsm" style={{ fontWeight: 600, color: EXPIRY_COLOR[c.clientsexpiring] || 'var(--nv)' }}>
                      {c.contractenddate || 'On Going'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex jb aic mt16">
            <div className="ts tg">Showing {showFrom}–{showTo} of {filtered.length} client{filtered.length !== 1 ? 's' : ''}</div>
            {totalPages > 1 && (
              <div className="pag">
                <button className="pgbtn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1}>‹</button>
                {safePage > 3 && (
                  <>
                    <button className="pgbtn" onClick={() => goTo(1)}>1</button>
                    {safePage > 4 && <span className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>}
                  </>
                )}
                {pageRange(safePage, totalPages).map(p => (
                  <button key={p} className={`pgbtn${p === safePage ? ' on' : ''}`} onClick={() => goTo(p)}>{p}</button>
                ))}
                {safePage < totalPages - 2 && (
                  <>
                    {safePage < totalPages - 3 && <span className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>}
                    <button className="pgbtn" onClick={() => goTo(totalPages)}>{totalPages}</button>
                  </>
                )}
                <button className="pgbtn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages}>›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
